import {
  type PreviewCoupon,
  applyCouponsToUnitPrice,
} from './applyCouponsToUnitPrice';

/** The two prices a listing (or its selected variant) carries before any coupon. */
export interface CouponPreviewBasePrice {
  /** The list price, before the store's own sale. */
  listPrice: number;
  /** What the store charges right now — the sale price when one is active. */
  currentPrice: number;
}

/**
 * The price the PDP shows with a previewed coupon, or `undefined` when the coupon
 * does not lower `currentPrice` (callers then keep their normal price render).
 *
 * A normal coupon comes off the current price, stacking on the store's sale. A
 * coupon flagged `nonStackingWithListingDiscount` (an auto-selling offer coupon,
 * minted for `list − offer price`) must not discount the same sale twice: the
 * buyer pays the lower of the current price and `list − coupon`. This mirrors the
 * order pricing in functions, so the PDP shows what checkout will charge.
 */
export function couponPreviewPrice(
  { listPrice, currentPrice }: CouponPreviewBasePrice,
  coupon: PreviewCoupon | null,
): number | undefined {
  if (coupon == null) return undefined;
  if (!coupon.nonStackingWithListingDiscount) {
    return applyCouponsToUnitPrice(currentPrice, [coupon]);
  }
  const couponPrice = applyCouponsToUnitPrice(listPrice, [coupon]) ?? listPrice;
  const price = Math.min(currentPrice, couponPrice);
  return price < currentPrice ? price : undefined;
}
